import React, { useState, useEffect } from 'react';
import { Container, Table, Form, InputGroup, Card, Row, Col, Badge, Spinner, Button, Tabs, Tab, Dropdown, Alert } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faHistory, faFilter, faFilePdf, faFileExcel, faDownload, faSync } from '@fortawesome/free-solid-svg-icons';
import MainLayout from '../../layouts/MainLayout';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const HistoriStock = () => {
  const [loading, setLoading] = useState(true);
  const [historyData, setHistoryData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30))); // Default 30 hari terakhir
  const [endDate, setEndDate] = useState(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(15);

  // Filter choices
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  
  useEffect(() => {
    fetchHistoryData();
  }, [activeTab, startDate, endDate, currentPage, statusFilter, sortOrder]);

  const fetchHistoryData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      
      if (!token) {
        toast.error('Sesi login telah berakhir');
        return;
      }
      
      // Format tanggal untuk API
      const startDateFormatted = startDate.toISOString().split('T')[0];
      const endDateFormatted = endDate.toISOString().split('T')[0];
      
      // Pilih endpoint berdasarkan activeTab
      let endpoint = '';
      let queryParams = `?start_date=${startDateFormatted}&end_date=${endDateFormatted}`;
      
      if (activeTab === 'all') {
        endpoint = 'https://rumahakrilik.id/api/inventory-transactions/';
      } else if (activeTab === 'in') {
        endpoint = 'https://rumahakrilik.id/api/inventory-transactions/?type=in';
      } else if (activeTab === 'out') {
        endpoint = 'https://rumahakrilik.id/api/inventory-transactions/?type=out';
      } else if (activeTab === 'manual_adjustment') {
        try {
          console.log('Fetching manual adjustments');
          const manualResponse = await axios.get('https://rumahakrilik.id/api/inventory-manual-adjustments/', {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              start_date: startDateFormatted,
              end_date: endDateFormatted
            }
          });
          
          console.log('Manual adjustment response:', manualResponse.data);
          
          // Format data untuk tampilan
          const adjustmentData = manualResponse.data.map(item => ({
            id: item.id,
            item_name: item.inventory_name || 'Unnamed Item',
            quantity: item.adjustment_quantity,
            previous_stock: item.previous_stock,
            new_stock: item.adjusted_stock,
            adjusted_by: item.adjusted_by_username || '-',
            adjustment_date: item.created_at,
            reason: item.reason || '-',
            type: item.adjustment_quantity >= 0 ? 'in' : 'out'
          }));
          
          setHistoryData(adjustmentData);
          setLoading(false);
          return;
        } catch (error) {
          console.error('Error fetching manual adjustments:', error);
          
          // Tampilkan pesan error spesifik jika ada respons dari server
          if (error.response) {
            console.log('Error response:', error.response);
            toast.error(`Gagal memuat data penyesuaian manual: ${error.response.status} ${error.response.statusText}`);
          } else if (error.request) {
            toast.error('Tidak dapat terhubung ke server. Periksa koneksi internet Anda.');
          } else {
            toast.error(`Gagal memuat data penyesuaian manual: ${error.message}`);
          }
          
          // Set data kosong dalam kasus error
          setHistoryData([]);
          setLoading(false);
          return;
        }
      }
      
      try {
        const response = await axios.get(endpoint + queryParams, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Handle both pagination and non-pagination responses
        if (response.data.results) {
          setHistoryData(response.data.results);
          setTotalPages(Math.ceil(response.data.count / itemsPerPage));
        } else {
          setHistoryData(response.data);
          setTotalPages(Math.ceil(response.data.length / itemsPerPage));
        }
      } catch (error) {
        console.error('Error fetching history data:', error);
        
        // Tampilkan pesan error spesifik
        if (error.response) {
          toast.error(`Gagal memuat data histori: ${error.response.status} ${error.response.statusText}`);
        } else if (error.request) {
          toast.error('Tidak dapat terhubung ke server. Periksa koneksi internet Anda.');
        } else {
          toast.error(`Gagal memuat data histori: ${error.message}`);
        }
        
        // Dalam kasus error, tampilkan data dummy jika ini adalah mode pengembangan
        if (process.env.NODE_ENV === 'development') {
          setHistoryData(generateDummyHistoryData());
          setTotalPages(3); // Demo pagination
          toast.info('Menampilkan data dummy untuk pengembangan');
        } else {
          setHistoryData([]);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const generateDummyHistoryData = () => {
    // Generate sample history data for demo purposes
    const statuses = ['pending', 'approved', 'rejected', 'received'];
    const usernames = ['admin', 'produksi', 'gudang', 'supervisor', 'staff'];
    const items = [
      'Akrilik Bening 3mm', 
      'Akrilik Susu 2mm', 
      'Lem Akrilik', 
      'Amplas Halus',
      'Stiker Vinyl'
    ];
    
    return Array.from({ length: 25 }, (_, i) => {
      const requestDate = new Date();
      requestDate.setDate(requestDate.getDate() - Math.floor(Math.random() * 30));
      
      const status = statuses[Math.floor(Math.random() * statuses.length)];
      let approvedDate = null;
      let receivedDate = null;
      
      if (status === 'approved' || status === 'received') {
        approvedDate = new Date(requestDate);
        approvedDate.setHours(approvedDate.getHours() + Math.floor(Math.random() * 24));
      }
      
      if (status === 'received') {
        receivedDate = new Date(approvedDate);
        receivedDate.setHours(receivedDate.getHours() + Math.floor(Math.random() * 48));
      }
      
      return {
        id: 1000 + i,
        item_name: items[Math.floor(Math.random() * items.length)],
        quantity: Math.floor(Math.random() * 20) + 1,
        requested_by: usernames[Math.floor(Math.random() * usernames.length)],
        request_date: requestDate.toISOString(),
        status,
        approved_date: approvedDate?.toISOString() || null,
        approved_by: status === 'approved' || status === 'received' ? 
                     usernames[Math.floor(Math.random() * usernames.length)] : null,
        received_date: receivedDate?.toISOString() || null,
        received_by: status === 'received' ? 
                     usernames[Math.floor(Math.random() * usernames.length)] : null,
        notes: Math.random() > 0.7 ? 'Dibutuhkan untuk produksi order #ORD-' + (5000 + i) : ''
      };
    });
  };
  
  // Helper function untuk format tanggal
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function untuk mendapatkan badge berdasarkan status
  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending':
        return <Badge bg="warning">Menunggu</Badge>;
      case 'approved':
        return <Badge bg="info">Diproses</Badge>;
      case 'rejected':
        return <Badge bg="danger">Ditolak</Badge>;
      case 'received':
        return <Badge bg="success">Diterima</Badge>;
      case 'cancelled':
        return <Badge bg="secondary">Dibatalkan</Badge>;
      default:
        return <Badge bg="light" text="dark">{status}</Badge>;
    }
  };

  // Fungsi untuk export ke Excel (dummy)
  const exportToExcel = () => {
    toast.info('Mengunduh data dalam format Excel...');
    // Implementasi export Excel sebenarnya akan ditambahkan di sini
  };

  // Fungsi untuk export ke PDF (dummy)
  const exportToPDF = () => {
    toast.info('Mengunduh data dalam format PDF...');
    // Implementasi export PDF sebenarnya akan ditambahkan di sini
  };

  // Filter data berdasarkan pencarian
  const filteredData = historyData.filter(item => 
    item.item_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.requested_by?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.notes?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <MainLayout>
      <Container fluid className="py-4">
        <h1 className="mb-4">
          <FontAwesomeIcon icon={faHistory} className="me-2" />
          Histori Permintaan Stock
        </h1>

        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={5}>
                <InputGroup className="mb-3">
                  <InputGroup.Text>
                    <FontAwesomeIcon icon={faSearch} />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Cari berdasarkan nama barang, pemohon, atau catatan..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Button 
                    variant="outline-secondary"
                    onClick={() => setSearchTerm('')}
                  >
                    Clear
                  </Button>
                </InputGroup>
              </Col>
              <Col md={7} className="d-flex justify-content-end">
                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={fetchHistoryData}
                  >
                    <FontAwesomeIcon icon={faSync} className="me-1" /> Refresh
                  </Button>
                  <Dropdown>
                    <Dropdown.Toggle variant="outline-success" id="export-dropdown" size="sm">
                      <FontAwesomeIcon icon={faDownload} className="me-1" /> Export
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={exportToExcel}>
                        <FontAwesomeIcon icon={faFileExcel} className="me-2" /> Excel
                      </Dropdown.Item>
                      <Dropdown.Item onClick={exportToPDF}>
                        <FontAwesomeIcon icon={faFilePdf} className="me-2" /> PDF
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </Col>
            </Row>
            
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Tanggal Awal</Form.Label>
                  <DatePicker
                    selected={startDate}
                    onChange={date => setStartDate(date)}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    dateFormat="dd/MM/yyyy"
                    className="form-control"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Tanggal Akhir</Form.Label>
                  <DatePicker
                    selected={endDate}
                    onChange={date => setEndDate(date)}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate}
                    dateFormat="dd/MM/yyyy"
                    className="form-control"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">Semua Status</option>
                    <option value="pending">Menunggu</option>
                    <option value="approved">Diproses</option>
                    <option value="received">Diterima</option>
                    <option value="rejected">Ditolak</option>
                    <option value="cancelled">Dibatalkan</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Urutan</Form.Label>
                  <Form.Select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                  >
                    <option value="newest">Terbaru Dulu</option>
                    <option value="oldest">Terlama Dulu</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        
        <Card>
          <Card.Header>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => {
                setActiveTab(k);
                setCurrentPage(1);
              }}
              className="mb-3"
            >
              <Tab eventKey="all" title="Semua Transaksi" />
              <Tab eventKey="in" title="Barang Masuk" />
              <Tab eventKey="out" title="Barang Keluar" />
              <Tab eventKey="manual_adjustment" title="Penyesuaian Manual" /> {/* Tambahkan tab ini */}
            </Tabs>
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Memuat data histori...</p>
              </div>
            ) : (
              <>
                {filteredData.length === 0 ? (
                  <Alert variant="info">
                    Tidak ada data histori untuk periode yang dipilih.
                  </Alert>
                ) : (
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        {activeTab === 'manual_adjustment' ? (
                          // Kolom untuk penyesuaian manual
                          <>
                            <th>Tanggal</th>
                            <th>Nama Item</th>
                            <th>Stok Sebelumnya</th>
                            <th>Stok Disesuaikan</th>
                            <th>Perubahan</th>
                            <th>Alasan</th>
                            <th>Diubah Oleh</th>
                          </>
                        ) : (
                          // Kolom untuk transaksi reguler
                          <>
                            <th>Tanggal</th>
                            <th>Nama Item</th>
                            <th>Tipe</th>
                            <th>Jumlah</th>
                            <th>Pemohon</th>
                            <th>Diproses Oleh</th>
                            <th>Referensi</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((item, index) => (
                        <tr key={index}>
                          {activeTab === 'manual_adjustment' ? (
                            // Konten untuk penyesuaian manual
                            <>
                              <td>
                                {new Date(item.adjustment_date).toLocaleDateString('id-ID', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </td>
                              <td>{item.item_name}</td>
                              <td>{item.previous_stock}</td>
                              <td>{item.new_stock}</td>
                              <td>
                                <Badge bg={item.quantity >= 0 ? 'success' : 'danger'}>
                                  {item.quantity >= 0 ? '+' : ''}{item.quantity}
                                </Badge>
                              </td>
                              <td>{item.reason}</td>
                              <td>{item.adjusted_by}</td>
                            </>
                          ) : (
                            // Konten untuk transaksi reguler
                            <>
                              <td>
                                {new Date(item.timestamp).toLocaleDateString('id-ID', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </td>
                              <td>{item.item_name}</td>
                              <td>
                                <Badge bg={item.type === 'in' ? 'success' : 'danger'}>
                                  {item.type === 'in' ? 'Masuk' : 'Keluar'}
                                </Badge>
                              </td>
                              <td>{item.quantity}</td>
                              <td>{item.requested_by || '-'}</td>
                              <td>{item.handled_by || '-'}</td>
                              <td>{item.reference || '-'}</td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-4">
                    <ul className="pagination">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setCurrentPage(1)}
                        >
                          &laquo;
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setCurrentPage(currentPage - 1)}
                        >
                          &lt;
                        </button>
                      </li>
                      
                      {[...Array(totalPages)].map((_, i) => {
                        // Tampilkan 5 halaman di sekitar halaman saat ini
                        if (
                          i === 0 || 
                          i === totalPages - 1 || 
                          (i >= currentPage - 2 && i <= currentPage + 2)
                        ) {
                          return (
                            <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => setCurrentPage(i + 1)}
                              >
                                {i + 1}
                              </button>
                            </li>
                          );
                        }
                        
                        // Tampilkan ellipsis untuk halaman yang dihilangkan
                        if (i === 1 && currentPage > 4) {
                          return <li key="start-ellipsis" className="page-item disabled"><span className="page-link">...</span></li>;
                        }
                        
                        if (i === totalPages - 2 && currentPage < totalPages - 3) {
                          return <li key="end-ellipsis" className="page-item disabled"><span className="page-link">...</span></li>;
                        }
                        
                        return null;
                      })}
                      
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setCurrentPage(currentPage + 1)}
                        >
                          &gt;
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setCurrentPage(totalPages)}
                        >
                          &raquo;
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </Container>
    </MainLayout>
  );
};

export default HistoriStock;